import { FC } from 'react';
import { useState } from 'react';
import { BarChart3, FileText, TrendingUp } from 'lucide-react';
import { TabList, PageWrapper } from '@/components/ui';
import {
  CompetitorTab,
  TopicsTab as TopicsTabComponent,
  PerformanceTab,
} from '@/features/topics';
import {
  useIdmCompetitors,
  useIdmPerformance,
  useIdmProposedTopics,
  useOrgId,
} from '@/hooks';
import { useUser } from '@/hooks';
import { useSelectedUser } from '@/contexts/SelectedUserContext';

const Topics: FC = () => {
  const [activeTab, setActiveTab] = useState<string>('competitor');
  // Pagination state for proposed topics
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Sorting state for proposed topics
  const [topicsSortBy, setTopicsSortBy] = useState<'category' | 'topicCount' | 'createdAt'>('createdAt');
  const [topicsSortOrder, setTopicsSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  // Search state
  const [topicsSearchQuery, setTopicsSearchQuery] = useState('');
  const [competitorSearchQuery, setCompetitorSearchQuery] = useState('');

  const user = useUser();
  const { selectedUser } = useSelectedUser();

  // Determine the target user ID for IDM operations
  // For external users: use their own ID
  // For internal users: use selectedUser ID if available, otherwise their own
  const targetUserId = user?.role === 'external'
    ? user?.id
    : (selectedUser?.id || user?.id);

  // Get orgId from user's company (for external users or when no selectedUser)
  const { orgId, loading: orgIdLoading, error: orgIdError } = useOrgId();

  // Determine if we should use userId or orgId
  const isInternalUserViewingExternal = user?.role === 'internal' && !!selectedUser?.id;
  const effectiveOrgId = isInternalUserViewingExternal ? undefined : orgId;
  const effectiveUserId = isInternalUserViewingExternal ? targetUserId : undefined;


  const {
    competitors,
    loading: competitorsLoading,
    error: competitorsError,
    fetchCompetitors,
  } = useIdmCompetitors(effectiveOrgId, !!(effectiveOrgId || effectiveUserId), effectiveUserId);

  // NEW IMPLEMENTATION: Using proposed topics API with pagination
  // Sort by createdAt DESC to show latest topics first
  const {
    proposedTopics,
    loading: topicsLoading,
    error: topicsError,
    total: topicsTotal,
    fetchProposedTopics,
  } = useIdmProposedTopics(
    effectiveOrgId,
    !!(effectiveOrgId || effectiveUserId),
    effectiveUserId,
    currentPage,
    pageSize,
    topicsSortBy,
    topicsSortOrder,
    isInternalUserViewingExternal
  );

  /* ============================================
   * OLD IMPLEMENTATION - COMMENTED FOR FUTURE REFERENCE
   * ============================================
   * 
   * This was the previous implementation that fetched topics with engagement estimates.
   * If you need to revert to this implementation, uncomment the code below and update
   * the TopicsTabComponent props accordingly.
   * 
   * const {
   *   topics,
   *   loading: topicsLoading,
   *   error: topicsError,
   * } = useIdmTopics(effectiveOrgId, !!(effectiveOrgId || effectiveUserId), effectiveUserId);
   */

  const {
    performance,
    loading: performanceLoading,
    error: performanceError,
  } = useIdmPerformance(
    effectiveOrgId,
    !!(effectiveOrgId || effectiveUserId),
    effectiveUserId,
    1,
    50
  );

  const tabs = [
    {
      id: 'competitor',
      label: 'Competitor Analysis',
      icon: <BarChart3 className="w-4 h-4" />,
    },
    {
      id: 'topics',
      label: 'Proposed Topics',
      icon: <FileText className="w-4 h-4" />,
    },
    {
      id: 'performance',
      label: 'Topic Performance',
      icon: <TrendingUp className="w-4 h-4" />,
    },
  ];

  // Show loading state (only for external users or when no selectedUser)
  if (!isInternalUserViewingExternal && orgIdLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 md:p-8 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  // Show error state (only for external users or when no selectedUser)
  if (!isInternalUserViewingExternal && (orgIdError || !orgId)) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 md:p-8 flex items-center justify-center">
        <div className="text-red-600">
          {orgIdError || 'No company found. Please complete your profile setup.'}
        </div>
      </div>
    );
  }

  // Show message for internal users when no user is selected
  if (user?.role === 'internal' && !selectedUser) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 md:p-8 flex items-center justify-center">
        <div className="text-gray-600">Please select a user to view their topics data.</div>
      </div>
    );
  }


  return (
    <PageWrapper
      title="Insight & Intelligence"
      subtitle="Analyze competitors, discover trending topics, and track content performance."
      tabs={
        <TabList
          tabs={tabs}
          activeTab={activeTab}
          onChange={setActiveTab}
        />
      }
    >

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'competitor' && (
          <CompetitorTab
            description="Details of competitor activity and top content sources."
            topSources={competitors}
            loading={competitorsLoading}
            error={competitorsError}
            onRefetch={async (sortBy?: string, sortOrder?: 'asc' | 'desc', search?: string) => {
              await fetchCompetitors({
                sortBy: sortBy as 'company' | 'articles' | 'avgScore' | 'avgPosts' | 'share',
                sortOrder,
                search,
              });
            }}
            onGlobalSearch={async (query: string) => {
              setCompetitorSearchQuery(query);
              await fetchCompetitors({
                search: query.trim() || undefined,
              });
            }}
            globalFilterValue={competitorSearchQuery}
          />
        )}

        {activeTab === 'topics' && (
          <TopicsTabComponent
            description="New topics suggested by IDM"
            rows={proposedTopics}
            loading={topicsLoading}
            error={topicsError}
            pagination={{
              pageIndex: currentPage - 1,
              pageSize,
              totalCount: topicsTotal,
            }}
            onPaginationChange={({ pageIndex, pageSize: nextSize }) => {
              const sizeChanged = nextSize !== pageSize;
              setPageSize(nextSize);
              setCurrentPage(sizeChanged ? 1 : pageIndex + 1);
            }}
            onRefetch={async (sortBy?: string, sortOrder?: 'ASC' | 'DESC', category?: string) => {
              if (sortBy) {
                setTopicsSortBy(sortBy as 'category' | 'topicCount' | 'createdAt');
              }
              if (sortOrder) {
                setTopicsSortOrder(sortOrder);
              }
              setCurrentPage(1); // Reset to first page on sort/search change
              // Refetch with updated parameters
              await fetchProposedTopics({
                sortBy: (sortBy || topicsSortBy) as 'category' | 'topicCount' | 'createdAt',
                sortOrder: sortOrder || topicsSortOrder,
                category: category || topicsSearchQuery.trim() || undefined,
                page: 1,
                limit: pageSize,
              });
            }}
            onGlobalSearch={async (query: string) => {
              setTopicsSearchQuery(query);
              setCurrentPage(1);
              await fetchProposedTopics({
                category: query.trim() || undefined,
                page: 1,
                limit: pageSize,
                sortBy: topicsSortBy,
                sortOrder: topicsSortOrder,
              });
            }}
            globalFilterValue={topicsSearchQuery}
          />
        )}

        {/* OLD IMPLEMENTATION - COMMENTED FOR FUTURE REFERENCE
        {activeTab === 'topics' && (
          <TopicsTabComponent
            description="New topics suggested by IXM with an estimate of expected engagement, content type, and the best template to use."
            rows={topics}
            loading={topicsLoading}
            error={topicsError}
            onViewDetails={handleViewTopicDetails}
            onViewPosts={handleViewPostsFromTopic}
            onUpdate={handleUpdateTopic}
          />
        )}
        */}

        {activeTab === 'performance' && (
          <PerformanceTab
            description=""
            legend={{
              news: "Blue tags = News-driven",
              company: "Green tags = Company-driven"
            }}
            rows={performance}
            loading={performanceLoading}
            error={performanceError}
          />
        )}
      </div>
    </PageWrapper >
  );
};

export default Topics;

